import React, { useState, useEffect, useRef } from "react";
import { Container, Box, Alert, CircularProgress } from "@mui/material";
import axios from "axios";
import io from "socket.io-client";
import TrikotGrid from "../components/TrikotGrid";
import BidDialog from "../components/BidDialog";
import SortingFilter from "../components/SortingFilter";
import { useAuth } from "../context/AuthContext";
import { API_BASE_URL, SOCKET_URL } from "../config";

const Dashboard = () => {
  const { user } = useAuth();
  const [trikots, setTrikots] = useState([]);
  const [error, setError] = useState(null);
  const [selectedTrikot, setSelectedTrikot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortOption, setSortOption] = useState("currentBid");
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  const fetchTrikots = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/trikots`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.data && response.data.trikots) {
        setTrikots(response.data.trikots);
        setError(null);
      } else {
        throw new Error("Ungültiges Datenformat vom Server");
      }
    } catch (err) {
      console.error("Error fetching trikots:", err);
      setError(
        "Fehler beim Laden der Trikots: " +
          (err.response?.data?.message ||
            err.message ||
            "Verbindung zum Server nicht möglich")
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const initSocket = () => {
      if (!socketRef.current) {
        socketRef.current = io(SOCKET_URL, {
          transports: ["websocket", "polling"], // Allow both transports
          autoConnect: false,
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          timeout: 20000,
        });

        socketRef.current.on("connect", () => {
          setIsConnected(true);
          setError(null);
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
          }
          if (trikots.length === 0) {
            fetchTrikots();
          }
        });

        socketRef.current.on("disconnect", (reason) => {
          // Nur loggen, wenn es kein normaler Disconnect ist (z.B. durch Cleanup)
          if (reason !== "io client disconnect" && process.env.NODE_ENV === "production") {
            console.log("Socket disconnected:", reason);
          }
          setIsConnected(false);
        });

        socketRef.current.on("connect_error", (error) => {
          // Nur in Production loggen, um Console-Spam zu vermeiden
          if (process.env.NODE_ENV === "production") {
            console.error("Socket connection error:", error);
          }
          setIsConnected(false);
          // Fehler nur setzen, wenn es kein normaler Disconnect ist
          // eslint-disable-next-line react-hooks/exhaustive-deps
          if (error.message !== "xhr poll error" && error.message !== "websocket error") {
            setError("Verbindungsfehler zum Server - Versuche erneut zu verbinden...");
          }
        });

        socketRef.current.on(
          "bidUpdate",
          ({ trikotId, currentBid, highestBidder, endTime }) => {
            setTrikots((prevTrikots) => {
              const newTrikots = prevTrikots.map((trikot) =>
                trikot._id === trikotId
                  ? {
                      ...trikot,
                      currentBid,
                      highestBidder,
                      endTime: endTime || trikot.endTime,
                    }
                  : trikot
              );

              if (sortOption === "currentBid") {
                return [...newTrikots].sort((a, b) =>
                  sortDirection === "asc"
                    ? a.currentBid - b.currentBid
                    : b.currentBid - a.currentBid
                );
              }
              return newTrikots;
            });
          }
        );

        socketRef.current.on("trikotUpdate", ({ trikotId, updatedTrikot }) => {
          setTrikots((prevTrikots) =>
            prevTrikots.map((trikot) =>
              trikot._id === trikotId ? { ...trikot, ...updatedTrikot } : trikot
            )
          );
        });

        // Connect the socket
        socketRef.current.connect();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (!isConnected) {
          if (socketRef.current) {
            socketRef.current.connect();
          } else {
            initSocket();
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    initSocket();

    // Cleanup function
    const timer = reconnectTimerRef.current;
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (timer) {
        clearTimeout(timer);
      }
      if (socketRef.current) {
        // Entferne alle Event-Listener
        socketRef.current.removeAllListeners();
        // Prüfe ob Socket noch verbunden ist, bevor disconnect aufgerufen wird
        if (socketRef.current.connected) {
          socketRef.current.disconnect();
        }
        socketRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  const [sortDirection, setSortDirection] = useState("desc");

  const getSortedTrikots = () => {
    return [...trikots].sort((a, b) => {
      let comparison = 0;

      switch (sortOption) {
        case "number":
          comparison = parseInt(a.number) - parseInt(b.number);
          break;
        case "currentBid":
          comparison = a.currentBid - b.currentBid;
          break;
        case "endTime":
          comparison = new Date(a.endTime) - new Date(b.endTime);
          break;
        default:
          return 0;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  };

  const handleSortChange = (newValue) => {
    setSortOption(newValue);
  };

  const handleDirectionChange = (newDirection) => {
    setSortDirection(newDirection);
  };

  const handleBid = (trikot) => {
    const isExpired = new Date(trikot.endTime) < new Date();
    if (isExpired) {
      setError("Diese Auktion ist bereits beendet.");
      return;
    }
    setSelectedTrikot(trikot);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Container maxWidth="xlg">
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              mt: { xs: 8, sm: 8 },
            }}
          >
            {error}
          </Alert>
        )}

        <SortingFilter
          sortOption={sortOption}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
          onDirectionChange={handleDirectionChange}
        />

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 3,
              mt: { xs: 2, sm: 2 },
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TrikotGrid
            key={sortOption}
            trikots={getSortedTrikots()}
            onBid={handleBid}
            isLoggedIn={Boolean(user)}
          />
        )}

        {selectedTrikot && (
          <BidDialog
            trikot={selectedTrikot}
            open={Boolean(selectedTrikot)}
            onClose={() => setSelectedTrikot(null)}
          />
        )}
      </Container>
    </Box>
  );
};

export default Dashboard;
